import React from "react";
import { QRCodeSVG } from "qrcode.react";
import { Apple, Smartphone, Play, Clock } from "lucide-react";
import { useApp } from "../AppContext";
import { PROGERE_MOBILE_STORES, getStoreUrl } from "../config/progere-modules";

/**
 * ProgereStoreQR — Bloc double QR code (App Store + Google Play).
 * Affiche les QR + boutons store officiels, avec badge "Bientôt disponible"
 * tant que `published: false` dans la config.
 *
 * Utilisation :
 *   <ProgereStoreQR variant="full" />          // pleine largeur, 2 colonnes
 *   <ProgereStoreQR variant="compact" />       // horizontal, plus petit
 */
export default function ProgereStoreQR({ variant = "full" }) {
  const { lang } = useApp();
  const isFr = lang === "fr";
  const compact = variant === "compact";

  const T = isFr ? {
    tagline: "PROGÈRE MOBILE",
    title: "Bientôt sur votre téléphone",
    subtitle: "L'écosystème PROGÈRE au complet, dans votre poche. Scannez pour être notifié dès le lancement.",
    scan: "Scannez avec votre appareil",
    comingSoon: "Bientôt disponible",
    getIt: "Obtenir sur",
    officialApp: "Application officielle",
    trust: "Publié par Groupe MJR Construction inc.",
  } : {
    tagline: "PROGÈRE MOBILE",
    title: "Coming soon to your phone",
    subtitle: "The full PROGÈRE ecosystem, in your pocket. Scan to be notified at launch.",
    scan: "Scan with your device",
    comingSoon: "Coming soon",
    getIt: "Get it on",
    officialApp: "Official application",
    trust: "Published by Groupe MJR Construction inc.",
  };

  const stores = [
    { key: "apple", cfg: PROGERE_MOBILE_STORES.appStore, Icon: Apple, gradient: "linear-gradient(135deg, #a2aaad, #f5f5f7)" },
    { key: "google", cfg: PROGERE_MOBILE_STORES.googlePlay, Icon: Play, gradient: "linear-gradient(135deg, #01875f, #4caf50)" },
  ];

  return (
    <section
      data-testid="store-qr-block"
      className={compact ? "rounded-2xl p-5" : "rounded-3xl p-8 sm:p-10"}
      style={{
        background: "linear-gradient(145deg, rgba(24,25,30,0.96), rgba(16,17,21,0.98))",
        border: "1px solid color-mix(in srgb, var(--pg-gold) 32%, var(--pg-border))",
        boxShadow: "0 20px 60px rgba(0,0,0,0.32), 0 0 30px rgba(242,188,46,0.06)",
      }}
    >
      <div className="flex flex-col md:flex-row md:items-center gap-8">
        {/* Copy */}
        <div className="flex-1 max-w-md">
          <div className="text-[11px] font-black uppercase tracking-widest" style={{ color: "var(--pg-gold)" }}>
            {T.tagline}
          </div>
          <h3 className={`${compact ? "text-xl" : "text-2xl sm:text-3xl"} font-heading font-black mt-2`} style={{ color: "var(--pg-text-primary)" }}>
            <span style={{ color: "var(--pg-gold)" }}>PRO</span><span style={{ color: "var(--pg-red)" }}>GÈRE</span>{" "}
            <span>{T.title}</span>
          </h3>
          <p className="mt-3 text-sm leading-relaxed" style={{ color: "var(--pg-text-secondary)" }}>
            {T.subtitle}
          </p>
          <div className="mt-4 inline-flex items-center gap-2 text-[10px] font-semibold uppercase tracking-widest px-3 py-1.5 rounded-full"
            style={{
              background: "color-mix(in srgb, var(--pg-gold) 12%, transparent)",
              color: "var(--pg-gold)",
              border: "1px solid color-mix(in srgb, var(--pg-gold) 36%, transparent)",
            }}
          >
            <Smartphone size={11} /> {T.officialApp}
          </div>
          <div className="mt-2 text-[10px]" style={{ color: "var(--pg-text-muted)" }}>{T.trust}</div>
        </div>

        {/* QR cards */}
        <div className="flex flex-col sm:flex-row gap-4 flex-shrink-0">
          {stores.map(({ key, cfg, Icon }) => {
            const url = getStoreUrl(cfg);
            return (
              <div
                key={key}
                data-testid={`store-qr-${key}`}
                className={compact ? "rounded-xl p-3 w-40" : "rounded-2xl p-4 w-44"}
                style={{
                  background: "var(--pg-bg-tertiary)",
                  border: "1px solid var(--pg-border)",
                }}
              >
                <div className="flex items-center gap-2 mb-3">
                  <Icon size={14} style={{ color: "var(--pg-text-primary)" }} />
                  <span className="text-xs font-semibold" style={{ color: "var(--pg-text-primary)" }}>{cfg.label}</span>
                </div>
                <div
                  className="rounded-lg overflow-hidden bg-white p-2 mx-auto"
                  style={{ width: compact ? 120 : 148, height: compact ? 120 : 148 }}
                >
                  <QRCodeSVG
                    value={url}
                    size={compact ? 104 : 132}
                    level="M"
                    marginSize={0}
                    fgColor="#0b0c0f"
                    bgColor="#ffffff"
                    imageSettings={{
                      // Logo center — small gold square
                      src: "data:image/svg+xml;utf8," + encodeURIComponent(
                        '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><rect width="24" height="24" rx="4" fill="#f2bc2e"/></svg>'
                      ),
                      height: compact ? 22 : 28,
                      width: compact ? 22 : 28,
                      excavate: true,
                    }}
                  />
                </div>
                {cfg.published ? (
                  <a
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`store-qr-link-${key}`}
                    className="mt-3 block text-center text-[10px] font-bold uppercase tracking-widest py-1.5 rounded-full"
                    style={{ background: "var(--pg-gold)", color: "var(--pg-text-inverse)" }}
                  >
                    {T.getIt} {cfg.label}
                  </a>
                ) : (
                  <div
                    className="mt-3 flex items-center justify-center gap-1 text-[10px] font-bold uppercase tracking-widest py-1.5 rounded-full"
                    style={{ background: "var(--pg-bg-secondary)", color: "var(--pg-text-muted)", border: "1px solid var(--pg-border)" }}
                  >
                    <Clock size={10} /> {T.comingSoon}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
